import React, { useEffect } from 'react';
import classNames from 'classnames';
import { DragDropContext } from 'react-beautiful-dnd';

import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';

import { StartBotsDialogueEditor } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import {
  MMdndContext,
  getMMdndEvents,
} from '../../../../../common/helpers/MMdndContext';

import { usePrimeState } from './use-state';
import './SlideoutDialogueEditor.pcss';
import { Button, Heading } from '@mm/components';
import Spinner from '../../../../../common/components/Spinner';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { useClickOutside } from '@mm/hooks';

import { useViewActions } from '@mm/state/actions/viewActions';
import TextInput from '../../../../../common/components/TextInput';
import { EditIcon } from '@mm/icons/general';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { PlatformType } from '@mm/state/model/CampaignsModel';

export type SlideoutDialogueEditorProps = {
  className?: string;
  chatbotId?: number;
  dialogueId?: number;
  name?: string;
  platforms?: PlatformType[];
};

export const SlideoutDialogueEditor = (props: SlideoutDialogueEditorProps) => {
  const {
    className,
    dialogueId,
    chatbotId,
    name,
    platforms = ['facebook'],
  } = props;
  const dndEvents = getMMdndEvents();

  const pathGraph = useNewGraphInstance(dialogueId);

  const [state, actions] = usePrimeState({ dialogueId });

  const viewActions = useViewActions();

  const dialogue = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, dialogueId?.toString() || ''),
  );

  const isSmsRelated = dialogue?.platforms?.includes('sms');

  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );

  const ref = useClickOutside<HTMLDivElement>(e => {
    if (Array.from((e.target as Element)?.classList)?.includes('js-overlay')) {
      viewActions.hideSlideoutMenu();
    }
  });

  useEffect(() => {
    actions.init();
  }, []);

  return (
    <div
      ref={ref}
      className={classNames(className, 'slideout-dialogue-editor')}
    >
      <MMdndContext.Provider value={dndEvents}>
        <DragDropContext
          onDragStart={dndEvents.onDragStart}
          onDragUpdate={dndEvents.onDragUpdate}
          onDragEnd={dndEvents.onDragEnd}
        >
          <Menu className="slideout-dialogue-editor__menu">
            <Menu.Header className="slideout-dialogue-editor__header">
              {!state.edit && (
                <>
                  <Heading size={3}>{state.name}</Heading>
                  {state.dialogue &&
                    ['start_bot', null].some(
                      kind => kind === state.dialogue?.kind,
                    ) && (
                      <EditIcon
                        className="slideout-dialogue-editor__header-action"
                        onClick={() => actions.edit(true)}
                      />
                    )}
                </>
              )}

              {state.edit && (
                <>
                  <TextInput
                    placeholder="Dialogue name"
                    onChange={actions.changeName}
                    value={state.name}
                    className="slideout-dialogue-editor__name-input"
                    maxLength={100}
                  />
                  {state.name &&
                  (dialogue.name || name) !== state.name.trim() &&
                  state.name.trim().length ? (
                    <Button
                      disabled={state.busy === 'name'}
                      css={{
                        container: 'slideout-dialogue-editor__header-action',
                      }}
                      onClick={actions.saveName}
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      css={{
                        container: 'slideout-dialogue-editor__header-action',
                      }}
                      onClick={() => actions.edit(false)}
                    >
                      Cancel
                    </Button>
                  )}
                  {state.busy === 'name' && <Spinner standalone show />}
                </>
              )}
            </Menu.Header>
            <Menu.Content className="slideout-dialogue-editor__content">
              {state.busy === 'init' && <Spinner show standalone={true} />}

              {state.busy !== 'init' &&
                dialogue &&
                widgets &&
                dialogueId &&
                chatbotId && (
                  <PathGraphContext.Provider value={pathGraph}>
                    <StartBotsDialogueEditor
                      chatbotId={chatbotId}
                      dialogueId={dialogueId}
                      widgets={widgets}
                      usedInSlideout
                      isSmsRelated={isSmsRelated}
                      platforms={platforms}
                    />
                  </PathGraphContext.Provider>
                )}
            </Menu.Content>
          </Menu>
        </DragDropContext>
      </MMdndContext.Provider>
    </div>
  );
};
